<template>
  <v-container fluid>
    <v-card class="pa-4">
      <!-- Header -->
      <div class="d-flex align-center mb-4">
        <v-icon color="#ce501b" size="large" class="mr-2">mdi-calendar-clock</v-icon>
        <h2 class="text-h5">Historial de Actividades</h2>
        <v-spacer></v-spacer>
        <v-btn
          icon
          @click="recargarDesdeInicio"
          :loading="loading && recientes.length === 0"
          color="#ffe8e1"
        >
          <v-icon color="#ce501b">mdi-refresh</v-icon>
        </v-btn>
      </div>

      <!-- Lista de items con infinite scroll -->
      <div 
        class="recientes-container" 
        @scroll="handleScroll"
        ref="scrollContainer"
      >
        <!-- Items -->
        <v-card
          v-for="(item, index) in recientes"
          :key="index"
          class="mb-3 reciente-item"
          elevation="2"
          hover
        >
          <v-card-text class="pa-4">
            <v-row align="center" dense>
              <!-- Acción y documento -->
              <v-col cols="12" sm="6" md="7" class="pr-sm-4">
                <div class="actividad-principal">
                  <div class="actividad-tipo">
                    {{ getPresentacionTipo(item.tipo).etiqueta }}
                  </div>
                  <div v-if="item.name_Toc" class="actividad-documento mt-1">
                    {{ item.name_Toc }}
                  </div>
                </div>
              </v-col>

              <!-- Metadata -->
              <v-col cols="12" sm="6" md="5" class="metadata-actividad pl-sm-4">
                <div class="metadata-linea">
                  <strong>Usuario:</strong> <span>{{ item.usuario }}</span>
                </div>
                <div class="metadata-linea">
                  <strong>Fecha:</strong> {{ formatearFecha(item.fechaCaptura) }}
                </div>
                <div class="actividad-adicional d-flex flex-wrap">
                  <span><strong>Plantilla:</strong> {{ item.plantilla || 'N/A' }}</span>
                  <span><strong>PC:</strong> {{ item.name_Pc }}</span>
                </div>
              </v-col>
            </v-row>

            <!-- Identificadores técnicos -->
            <div class="identificadores-tecnicos d-flex flex-wrap mt-3 pt-3">
              <span><strong>ID de evento:</strong> {{ item.codigo || 'N/A' }}</span>
              <span><strong>TocId:</strong> {{ item.tocId }}</span>
              <!-- <span><strong>Versión:</strong> {{ item.version || 'N/A' }}</span> -->
            </div>
          </v-card-text>
        </v-card>

        <!-- Indicador de carga al final -->
        <div v-if="loading" class="text-center py-4">
          <v-progress-circular
            indeterminate
            color="primary"
            size="50"
          ></v-progress-circular>
          <p class="mt-2 text-caption">Cargando más documentos...</p>
        </div>

        <!-- Mensaje cuando no hay más datos -->
        <div v-if="!loading && !hayMasDatos && recientes.length > 0" class="text-center py-4 text-caption text-grey">
          <v-icon>mdi-check-circle</v-icon>
          No hay más documentos para mostrar
        </div>

        <!-- Mensaje cuando no hay datos -->
        <div v-if="!loading && recientes.length === 0" class="text-center py-8">
          <v-icon size="64" color="grey">mdi-file-document-outline</v-icon>
          <p class="text-h6 text-grey mt-2">No hay documentos recientes</p>
        </div>
      </div>
    </v-card>
  </v-container>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import axios from '@/plugins/axios'
import Swal from 'sweetalert2'

const recientes = ref([])
const loading = ref(false)
const hayMasDatos = ref(true)
const paginaActual = ref(1)
const itemsPorPagina = ref(20) // Cargar 20 items a la vez
const scrollContainer = ref(null)

onMounted(async () => {
  await cargarRecientes()
})

const cargarRecientes = async () => {
  if (loading.value || !hayMasDatos.value) return
  
  loading.value = true
  
  try {
    const idUsuario = localStorage.getItem('idUsuario')
    
    if (!idUsuario) {
      Swal.fire({
        icon: 'error',
        iconColor: '#ce501b',
        title: 'Error',
        text: 'No se pudo obtener la información del usuario',
        customClass: {
          title: 'swal-title-custom',
          container: 'swal-sobre-modal'
        }
      })
      return
    }

    const response = await axios.get(`/api/Recientes/${idUsuario}`)
    
    if (response.data.success) {
      const nuevosItems = response.data.data
      
      // Simular paginación del lado del cliente
      const inicio = (paginaActual.value - 1) * itemsPorPagina.value
      const fin = inicio + itemsPorPagina.value
      const itemsPaginados = nuevosItems.slice(inicio, fin)
      
      if (itemsPaginados.length > 0) {
        recientes.value = [...recientes.value, ...itemsPaginados]
        paginaActual.value++
      }
      
      // Verificar si hay más datos
      if (fin >= nuevosItems.length) {
        hayMasDatos.value = false
      }
      
    //console.log('Recientes cargados:', recientes.value.length)     //Usarlo solo para ver los errores en el testeo de Software
    } else {
      Swal.fire({
        icon: 'error',
        iconColor: '#ce501b',
        title: 'Error',
        text: response.data.mensaje || 'No se pudieron cargar los recientes',
        customClass: {
          title: 'swal-title-custom',
          container: 'swal-sobre-modal'
        }
      })
    }
  } catch (error) {
    //console.error('Error al cargar recientes:', error)             //Usarlo solo para ver los errores en el testeo de Software
    Swal.fire({
      icon: 'error',
      iconColor: '#ce501b',
      title: 'Error',
      text: 'Hubo un problema al cargar los documentos recientes',
      customClass: {
        title: 'swal-title-custom',
        container: 'swal-sobre-modal'
      }
    })
  } finally {
    loading.value = false
  }
}

const handleScroll = (event) => {
  const { scrollTop, scrollHeight, clientHeight } = event.target
  
  // Si llegamos al 80% del scroll, cargar más
  if (scrollTop + clientHeight >= scrollHeight * 0.8) {
    cargarRecientes()
  }
}

const recargarDesdeInicio = () => {
  recientes.value = []
  paginaActual.value = 1
  hayMasDatos.value = true
  cargarRecientes()
}

const formatearFecha = (fecha) => {
  if (!fecha) return 'N/A'
  const date = new Date(fecha)
  return date.toLocaleDateString('es-PE', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  })
}

const getPresentacionTipo = (tipo) => {
  const presentaciones = {
    'Visualización de documentos': {
      etiqueta: 'Visualización de documento'
    },
    'Inserto Indexacion': {
      etiqueta: 'Documento indexado'
    },
    'Inicio sesión': {
      etiqueta: 'Inicio de sesión'
    },
    'cerrar sesión': {
      etiqueta: 'Cierre de sesión'
    },
    'Cambio Contrasenia': {
      etiqueta: 'Cambio de contraseña'
    }
  }

  return presentaciones[tipo] || {
    etiqueta: tipo || 'N/A'
  }
}


</script>

<style scoped>
.recientes-container {
  max-height: calc(100vh - 250px);
  overflow-y: auto;
  padding-right: 8px;
}

/* Personalizar scrollbar */
.recientes-container::-webkit-scrollbar {
  width: 8px;
}

.recientes-container::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 10px;
}

.recientes-container::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 10px;
}

.recientes-container::-webkit-scrollbar-thumb:hover {
  background: #555;
}

.reciente-item {
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.reciente-item:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 20px rgba(0,0,0,0.15) !important;
}

.actividad-principal {
  min-width: 0;
}

.actividad-tipo {
  color: #004d82;
  font-size: 1.05rem;
  font-weight: 700;
  line-height: 1.3;
}

.actividad-documento {
  color: #303840;
  font-size: 0.95rem;
  font-weight: 600;
  overflow-wrap: anywhere;
}

.metadata-actividad,
.actividad-adicional {
  color: #4b5563;
  font-size: 0.82rem;
  line-height: 1.3;
}

.metadata-actividad {
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 4px;
  border-left: 1px solid #e1e5e9;
}

.metadata-linea {
  font-size: 0.82rem;
  font-weight: 400;
  line-height: 1.3;
  overflow-wrap: anywhere;
}

.metadata-linea span {
  font-size: inherit;
  font-weight: inherit;
}

.actividad-adicional {
  gap: 4px 18px;
  color: #626b74;
  font-size: 0.82rem;
  font-weight: 400;
  line-height: 1.3;
}

.actividad-adicional span {
  font-size: inherit;
  font-weight: inherit;
}

.metadata-actividad strong,
.actividad-adicional strong {
  font-weight: 600;
}

.identificadores-tecnicos {
  gap: 6px 18px;
  border-top: 1px solid #e5e7eb;
  color: #737b84;
  font-size: 0.74rem;
}

.identificadores-tecnicos strong {
  color: #5d6670;
  font-weight: 600;
}

@media (max-width: 600px) {
  .recientes-container {
    padding-right: 2px;
  }

  .metadata-actividad {
    margin-top: 10px;
    padding-top: 10px !important;
    border-top: 1px solid #e1e5e9;
    border-left: 0;
  }

  .actividad-tipo {
    font-size: 1rem;
  }

  .identificadores-tecnicos {
    gap: 5px 12px;
  }
}
</style>

<style>

.swal-title-custom {
  color: #004d82 !important;
  font-size: 1.3rem !important;
}

.swal-text-custom {
  color: #555555 !important;
  font-size: 0.95rem !important;
}

.swal-btn-custom {
  background-color: #ce501b !important;
  color: #ffffff !important;
  border-radius: 6px !important;
  padding: 8px 24px !important;
}

.swal-btn-custom:hover {
  background-color: #a83d14 !important;
}

.swal-btn-confirmar {
  color: white !important;
}

</style>

<style>
.swal-sobre-modal {
  z-index: 10000 !important;
}
</style>
